import { useEffect } from 'react';
import { toast } from 'sonner';
import { IconButton } from '@/components/ui/button';
import { SegmentationPhase, segmentationStore } from '@segmentation/store/segmentationStore.ts';
import useSegmentationStore from '@segmentation/hooks/useSegmentationStore.ts';
import { gumEditStore } from '@segmentation/store/gumEditStore';
import { GumEditTool } from '@segmentation/three/GumEditTool';
import { toolManager } from '@modules/three/tools/ToolManager';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore';

/** The jaw being edited in the current gum-edit phase (null outside the phase). */
const getPhaseJaw = () => {
  const state = segmentationStore.getState();
  if (state.segmentationPhase === SegmentationPhase.GUM_EDIT_LOWER) return state.jaws?.lowerJaw ?? null;
  if (state.segmentationPhase === SegmentationPhase.GUM_EDIT_UPPER) return state.jaws?.upperJaw ?? null;
  return null;
};

/**
 * UI for the gum-edit phase (before each jaw's tooth selection): pick two
 * border vertices and reconstruct the gum across the scan bay between them.
 * (Gum trimming happens later, in the post-segmentation review step.)
 */
export const GumEditUI = () => {
  const { setSegmentationPhase, segmentationPhase, jaws } = useSegmentationStore();
  const isUpper = segmentationPhase === SegmentationPhase.GUM_EDIT_UPPER;
  const jaw = isUpper ? jaws?.upperJaw ?? null : jaws?.lowerJaw ?? null;
  const selectedCount = gumEditStore((s) => s.selectedCount);
  const setSelectedCount = gumEditStore((s) => s.setSelectedCount);
  const busy = gumEditStore((s) => s.busy);
  const setBusy = gumEditStore((s) => s.setBusy);

  // Tool lifecycle: active for the whole phase, torn down on leave.
  useEffect(() => {
    if (!jaw) return;
    jaw.prepareGumEdit();
    const tool = new GumEditTool({ getJaw: getPhaseJaw });
    toolManager.register(tool);
    threeInteractionStore.getState().setActiveTool(tool.name);
    return () => {
      threeInteractionStore.getState().setActiveTool(null);
      toolManager.unregister(tool.name);
      jaw.clearGumEditVisuals();
      gumEditStore.getState().reset();
    };
  }, [jaw]);

  if (!jaw) return null;

  const handleBridge = () => {
    setBusy(true);
    try {
      const facesAdded = jaw.bridgeSelectedGumGap();
      if (facesAdded === null) {
        toast.error('Could not fill the gap', {
          description: 'Pick two vertices on the same gum border with a bay between them.',
        });
      } else {
        setSelectedCount(0);
        toast.success('Gum gap filled', { description: `${facesAdded} triangles created` });
      }
    } catch (error) {
      toast.error('Gum fill failed', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = () => {
    jaw.clearGumEditVisuals();
    setSegmentationPhase(
      isUpper ? SegmentationPhase.TEETH_SELECTION_UPPER : SegmentationPhase.TEETH_SELECTION_LOWER,
    );
  };

  return (
    <section className="flex w-full flex-col gap-5 rounded-3xl border border-primary/40 bg-content/80 p-6 text-white shadow-xl shadow-primary/20 backdrop-blur">
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">
          Gum editing · {isUpper ? 'upper' : 'lower'} jaw
        </p>
        <h2 className="text-2xl font-semibold tracking-tight">Bridge scan gaps</h2>
        <p className="text-sm text-white/70">
          Click two border vertices on the gum edge — the cursor snaps to the nearest border point. Confirm to
          reconstruct the gum between them.
        </p>
      </header>

      <div className="space-y-4">
        <div className="rounded-2xl border border-content-highlight/50 bg-content-dark/60 px-4 py-3 text-sm text-white/90">
          Selected border vertices: <span className="font-semibold">{selectedCount} / 2</span>
        </div>
        <div className="flex gap-2">
          <IconButton
            onClick={handleBridge}
            disabled={selectedCount !== 2 || busy}
            variant="ghost-primary"
            size="lg"
            className="flex-1 rounded-2xl border border-primary/50 bg-primary/80 text-xs font-semibold uppercase tracking-[0.3em] hover:bg-primary disabled:opacity-40"
          >
            {busy ? 'Filling…' : 'Connect & fill'}
          </IconButton>
          <IconButton
            onClick={() => {
              jaw.clearGumEditSelection();
              setSelectedCount(0);
            }}
            disabled={selectedCount === 0 || busy}
            variant="ghost-light"
            size="lg"
            className="rounded-2xl px-4 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
          >
            Clear
          </IconButton>
        </div>
        <IconButton
          onClick={handleContinue}
          disabled={busy}
          variant="ghost-light"
          size="lg"
          className="w-full rounded-2xl border border-content-highlight/50 text-xs font-semibold uppercase tracking-[0.3em]"
        >
          Start tooth selection
        </IconButton>
      </div>
    </section>
  );
};
